import { IndicatorType } from '@risksmart-app/domain/src/types/consts/indicator-type';

import { roundToTwoDecimals } from '@/utils/numberUtils';

export const THREE_MONTH_AVERAGE_WINDOW_DAYS = 90;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type ThreeMonthAverageResult =
  | { kind: 'empty' }
  | { kind: 'na' }
  | { kind: 'value'; value: number };

type ResultRecord = {
  TargetValueNum?: number | null;
  TargetValueTxt?: string | null;
  ResultDate: string;
};

const startOfUtcDay = (input: Date | string): Date => {
  const d = typeof input === 'string' ? new Date(input) : new Date(input);

  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  );
};

/**
 * Computes the rolling 90-day arithmetic mean for an indicator's results.
 *
 * Performance note: assumes `results` is sorted by `ResultDate` descending
 * (the shape returned by the indicator register query — see
 * `getIndicators.graphql` which orders by `ResultDate: desc_nulls_last`).
 * Under that ordering the loop accumulates `sum`/`count` in a single pass and
 * breaks as soon as a result older than the cutoff is seen, making this O(k)
 * in the number of in-window rows rather than O(n) over the full history.
 *
 * If callers ever pass an unsorted array, the early-break would under-count.
 * Sorting is the caller's contract.
 */
export const computeThreeMonthAverage = (
  type: string | null | undefined,
  results: readonly ResultRecord[] | null | undefined,
  now: Date = new Date()
): ThreeMonthAverageResult => {
  if (type !== IndicatorType.Number) {
    return { kind: 'na' };
  }

  if (!results || results.length === 0) {
    return { kind: 'empty' };
  }

  const cutoffMs =
    startOfUtcDay(now).getTime() - THREE_MONTH_AVERAGE_WINDOW_DAYS * MS_PER_DAY;

  let sum = 0;
  let count = 0;
  for (const r of results) {
    const resultDayMs = startOfUtcDay(r.ResultDate).getTime();
    if (resultDayMs < cutoffMs) {
      // Sorted desc: every subsequent row is also outside the window.
      break;
    }
    if (r.TargetValueNum === null || r.TargetValueNum === undefined) {
      // Null submissions are excluded but do not terminate the window scan.
      continue;
    }
    sum += r.TargetValueNum;
    count++;
  }

  if (count === 0) {
    return { kind: 'empty' };
  }

  return { kind: 'value', value: roundToTwoDecimals(sum / count) };
};

export const formatThreeMonthAverage = (
  result: ThreeMonthAverageResult
): string => {
  if (result.kind === 'na') {
    return 'N/A';
  }
  if (result.kind === 'empty') {
    return '—';
  }

  return result.value.toFixed(2);
};

/**
 * Pure comparator for the Three Month Average column.
 *
 * AC-l-a + AC-l-b: numeric values sort by magnitude; `null`-equivalent
 * rows (`empty` / `na` states, both represented as `ThreeMonthAverageValue
 * === null`) pin to the BOTTOM in both directions.
 *
 * Cloudscape multiplies the returned value by ±1 for asc/desc, so we
 * pre-flip the null-vs-finite cases based on the live sort direction so
 * the net sign after Cloudscape's multiplication is always "null after
 * finite".
 */
export const compareThreeMonthAverage = (
  aValue: number | null,
  bValue: number | null,
  isDescending: boolean
): number => {
  if (aValue === null && bValue === null) {
    return 0;
  }
  if (aValue === null) {
    return isDescending ? -1 : 1;
  }
  if (bValue === null) {
    return isDescending ? 1 : -1;
  }

  return aValue - bValue;
};
